import React, {useState, useCallback, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'

const QuantityPicker = (props) => {
    const intl = useIntl()
    const productName = props.productName
    const {min = 1, max = 99, step = 1, value: controlledValue, onChange} = props
    const [internalValue, setInternalValue] = useState(controlledValue ?? min)
    const inputRef = useRef(null)

    const value = controlledValue ?? internalValue
    const setValue = useCallback(
        (newVal) => {
            const clamped = Math.min(Math.max(newVal, min), max)
            setInternalValue(clamped)
            onChange?.(String(clamped), clamped)
        },
        [min, max, onChange]
    )

    const handleIncrement = () => setValue(value + step)
    const handleDecrement = () => setValue(value - step)

    const handleInputChange = (e) => {
        const num = parseInt(e.target.value, 10)
        if (!isNaN(num)) setValue(num)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault()
            handleIncrement()
        } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            handleDecrement()
        }
    }

    const handleFocus = (e) => {
        e.target.select()
        props.onFocus?.call(this, e)
    }

    const incAriaLabel = intl.formatMessage(
        {
            defaultMessage: 'Increment Quantity',
            id: 'product_view.label.assistive_msg.quantity_increment'
        },
        {productName}
    )
    const decAriaLabel = intl.formatMessage(
        {
            defaultMessage: 'Decrement Quantity',
            id: 'product_view.label.assistive_msg.quantity_decrement'
        },
        {productName}
    )
    const inputAriaLabel = intl.formatMessage({
        defaultMessage: 'Quantity',
        id: 'product_view.label.quantity'
    })

    return (
        <div className="flex items-center justify-between border-2 border-solid border-foreground rounded-[0.5625rem] w-full h-[3.125rem]">
            <button
                aria-label={decAriaLabel}
                onClick={handleDecrement}
                disabled={value <= min}
                className="h-full flex flex-col justify-center pt-2 pl-1.5 font-grotesque font-light text-[1.5625rem]"
            >
                <FormattedMessage
                    id="product_view.label.quantity_decrement"
                    // HTML &minus;
                    defaultMessage={'\u2212'}
                    data-testid="quantityPicker-FormattedMessage"
                />
            </button>
            <input
                ref={inputRef}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                aria-label={inputAriaLabel}
                inputMode="numeric"
                className="block w-[calc(100%_-_2.875rem)] pt-1 text-center eyebrow-sm tracking-[0.04375rem]"
            />
            <button
                aria-label={incAriaLabel}
                onClick={handleIncrement}
                disabled={value >= max}
                className="h-full flex flex-col justify-center pt-2 pr-1.5 font-grotesque font-light text-[1.5625rem]"
            >
                <FormattedMessage
                    id="product_view.label.quantity_increment"
                    defaultMessage="+"
                    data-testid="quantityPicker-FormattedMessage"
                />
            </button>
        </div>
    )
}

QuantityPicker.propTypes = {
    productName: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    value: PropTypes.number,
    onChange: PropTypes.func,
    onFocus: PropTypes.func
}

export default QuantityPicker
